import { useWebhookHook } from "@/hooks/use-webhook.hook.ts";
import { useWebhookRedeliver } from "@/queries/settings/use-webhooks.query.ts";
import { ApiWebhookDelivery, ApiWebhookDeliveryStatus } from "@/types/webhook.types.ts";
import { Button, Modal, ModalProps, Stack, Tag, Text, Title, useToasts } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

interface WebhookDeliveryModalProps extends ModalProps {
  delivery: ApiWebhookDelivery;
}

function JsonBlock({ value, redact }: { value: unknown; redact: (value: unknown) => unknown }) {
  let displayValue = value;
  if (typeof value === "string") {
    try {
      displayValue = JSON.parse(value) as unknown;
    } catch {
      displayValue = value;
    }
  }
  const safeValue = redact(displayValue);

  return (
    <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-xs bg-dark/5 p-4 text-xs">
      {typeof safeValue === "string" ? safeValue : JSON.stringify(safeValue, null, 2)}
    </pre>
  );
}

export function WebhookDeliveryModal({ delivery, ...props }: WebhookDeliveryModalProps) {
  const { t } = useTranslation();
  const { showInfo, showError } = useToasts();
  const { redactSensitiveData, redactUrl } = useWebhookHook();
  const redeliver = useWebhookRedeliver();

  const handleRedeliver = async () => {
    try {
      const result = await redeliver.mutateAsync(delivery.id);
      if (result.status === ApiWebhookDeliveryStatus.delivered) {
        showInfo({
          title: t("core:webhook_redelivery_succeeded"),
          description: result.response_status ? `HTTP ${result.response_status}` : undefined,
        });
      } else {
        showError({
          title: t("core:webhook_redelivery_failed"),
          description:
            result.error_message ??
            (result.response_status ? `HTTP ${result.response_status}` : undefined),
        });
      }
    } catch {
      return;
    }
  };

  return (
    <Modal
      {...props}
      title={t("core:webhook_delivery_details")}
      withCloser
      size="lg"
      actions={
        <Stack>
          <Button size="lg" isLoading={redeliver.isPending} onClick={() => void handleRedeliver()}>
            {t("core:webhook_redeliver")}
          </Button>
        </Stack>
      }
    >
      <Stack gap="lg">
        <Stack direction="horizontal" gap="sm" items="center">
          <Tag
            appearance="filled"
            variant={
              delivery.status === ApiWebhookDeliveryStatus.delivered
                ? "positive"
                : delivery.status === ApiWebhookDeliveryStatus.failed
                  ? "danger"
                  : "secondary"
            }
          >
            {t(`core:webhook_delivery_status_${delivery.status}`)}
          </Tag>
          <Text className="text-secondary">HTTP {delivery.response_status ?? "—"}</Text>
        </Stack>

        <Stack gap="xs">
          <Title header={t("common:request")} size="xs" caption={redactUrl(delivery.request_url)} />
          <Title header={t("common:request_headers")} size="xs" paddingTop />
          <JsonBlock value={delivery.request_headers} redact={redactSensitiveData} />
          <Title header={t("common:request_payload")} size="xs" paddingTop />
          <JsonBlock value={delivery.request_payload} redact={redactSensitiveData} />
        </Stack>

        <Stack gap="xs">
          <Title header={t("core:webhook_response")} size="xs" />
          {delivery.response_headers && (
            <JsonBlock value={delivery.response_headers} redact={redactSensitiveData} />
          )}
          {(delivery.response_body?.trim() ?? "").length > 0 && (
            <JsonBlock value={delivery.response_body} redact={redactSensitiveData} />
          )}
        </Stack>

        {delivery.error_message && (
          <Stack gap="xs">
            <Title header={t("common:error")} size="xs" />
            <JsonBlock value={delivery.error_message} redact={redactSensitiveData} />
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
